import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Form, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { reauthenticateWithPassword, recentlyReauthenticated } from "../services/reauth";

const ReauthContext = createContext({ requireReauth: async () => true });

/**
 * `const ok = await requireReauth()` asks for the password again before a
 * sensitive action (role, user, license, data seeding) unless it was entered
 * in the last few minutes. Resolves false when the user cancels.
 */
export function useReauth() {
  return useContext(ReauthContext);
}

export default function ReauthProvider({ children }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useRef(null);

  const finish = (result) => {
    setOpen(false);
    setPassword("");
    setError("");
    const resolve = pending.current;
    pending.current = null;
    if (resolve) resolve(result);
  };

  const requireReauth = useCallback(() => {
    if (recentlyReauthenticated()) return Promise.resolve(true);
    if (pending.current) pending.current(false);
    return new Promise((resolve) => {
      pending.current = resolve;
      setOpen(true);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    setError("");
    try {
      await reauthenticateWithPassword(password);
      finish(true);
    } catch (err) {
      const code = err?.code || "";
      setError(t(code.includes("too-many-requests") ? "auth.errors.tooManyRequests" : "reauth.wrongPassword"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ReauthContext.Provider value={{ requireReauth }}>
      {children}
      <Modal isOpen={open} toggle={() => finish(false)} centered data-testid="reauth-modal">
        <Form onSubmit={submit}>
          <ModalHeader toggle={() => finish(false)}>{t("reauth.title")}</ModalHeader>
          <ModalBody>
            <p className="small text-muted">{t("reauth.body")}</p>
            {error && <Alert color="danger" className="py-2">{error}</Alert>}
            <Input type="password" autoFocus autoComplete="current-password" aria-label={t("reauth.password")}
              placeholder={t("reauth.password")} value={password} onChange={(e) => setPassword(e.target.value)} />
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" outline onClick={() => finish(false)}>{t("common.cancel")}</Button>
            <Button type="submit" color="primary" disabled={busy || !password}>
              {busy ? <Spinner size="sm" /> : t("reauth.confirm")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </ReauthContext.Provider>);

}
